import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { meApi, spaceApi } from '../services/api';
import { useSpaceId } from '../contexts/SpaceContext';
import type {
  Agent,
  CreateAgentInput,
  UpdateAgentInput,
  InvokeInput,
} from '../types';
import { isPlatformEntity } from '../types';

// ---------------------------------------------------------------------------
// Space-scoped — used by every console agents page (under /console/spaces/:id)
// ---------------------------------------------------------------------------

function useAgentApi() {
  const spaceId = useSpaceId();
  return useMemo(() => spaceApi(spaceId).agents, [spaceId]);
}

export function useSpaceAgents() {
  const spaceId = useSpaceId();
  const api = useAgentApi();
  return useQuery({
    queryKey: ['space', spaceId, 'agents'],
    queryFn: () => api.list(),
  });
}

export function useSpaceAgent(agentId: string | undefined) {
  const spaceId = useSpaceId();
  const api = useAgentApi();
  return useQuery({
    queryKey: ['space', spaceId, 'agents', agentId],
    queryFn: () => api.get(agentId!),
    enabled: !!agentId,
  });
}

export function useCreateSpaceAgent() {
  const spaceId = useSpaceId();
  const api = useAgentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAgentInput) => api.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'agents'] });
    },
  });
}

export function useUpdateSpaceAgent() {
  const spaceId = useSpaceId();
  const api = useAgentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, input }: { agentId: string; input: UpdateAgentInput }) =>
      api.update(agentId, input),
    onSuccess: (data: Agent) => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'agents'] });
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'agents', data.agentId] });
    },
  });
}

export function useDeleteSpaceAgent() {
  const spaceId = useSpaceId();
  const api = useAgentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => api.delete(agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'agents'] });
    },
  });
}

export function usePublishSpaceAgent() {
  const spaceId = useSpaceId();
  const api = useAgentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, description }: { agentId: string; description?: string }) =>
      api.publish(agentId, description),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['space', spaceId, 'agents', variables.agentId],
      });
      queryClient.invalidateQueries({
        queryKey: ['space', spaceId, 'agents', variables.agentId, 'versions'],
      });
    },
  });
}

export function useSpaceAgentVersions(agentId: string | undefined) {
  const spaceId = useSpaceId();
  const api = useAgentApi();
  return useQuery({
    queryKey: ['space', spaceId, 'agents', agentId, 'versions'],
    queryFn: () => api.listVersions(agentId!),
    enabled: !!agentId,
  });
}

export function useSpaceAgentVersion(
  agentId: string | undefined,
  version: number | undefined,
) {
  const spaceId = useSpaceId();
  const api = useAgentApi();
  return useQuery({
    queryKey: ['space', spaceId, 'agents', agentId, 'versions', version],
    queryFn: () => api.getVersion(agentId!, version!),
    enabled: !!agentId && version !== undefined,
  });
}

export function useDeleteSpaceAgentVersion() {
  const spaceId = useSpaceId();
  const api = useAgentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, version }: { agentId: string; version: number }) =>
      api.deleteVersion(agentId, version),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['space', spaceId, 'agents', variables.agentId, 'versions'],
      });
    },
  });
}

export function useSpaceAgentAliases(agentId: string | undefined) {
  const spaceId = useSpaceId();
  const api = useAgentApi();
  return useQuery({
    queryKey: ['space', spaceId, 'agents', agentId, 'aliases'],
    queryFn: () => api.listAliases(agentId!),
    enabled: !!agentId,
  });
}

export function useCreateSpaceAgentAlias() {
  const spaceId = useSpaceId();
  const api = useAgentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      agentId,
      aliasName,
      targetVersion,
      description,
    }: {
      agentId: string;
      aliasName: string;
      targetVersion: number;
      description?: string;
    }) => api.createAlias(agentId, aliasName, targetVersion, description),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['space', spaceId, 'agents', variables.agentId, 'aliases'],
      });
    },
  });
}

export function useDeleteSpaceAgentAlias() {
  const spaceId = useSpaceId();
  const api = useAgentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, aliasName }: { agentId: string; aliasName: string }) =>
      api.deleteAlias(agentId, aliasName),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['space', spaceId, 'agents', variables.agentId, 'aliases'],
      });
    },
  });
}

export function useInvokeSpaceAgent() {
  const api = useAgentApi();
  return useMutation({
    mutationFn: ({ agentId, input }: { agentId: string; input: InvokeInput }) =>
      api.invoke(agentId, input),
  });
}

/**
 * Sub-agent selection list, scoped to the current space. Filters out the
 * current agent and platform/global agents.
 */
export function useSpaceAgentsForSubAgentSelection(currentAgentId?: string) {
  const { data, isLoading } = useSpaceAgents();
  const agents = useMemo(() => {
    const items = data?.items ?? [];
    return items.filter(
      (a) => a.agentId !== currentAgentId && !isPlatformEntity(a),
    );
  }, [data, currentAgentId]);
  return { agents, isLoading };
}

// ---------------------------------------------------------------------------
// Cross-space — used by Builder canvas, RecentActivitiesPanel, and any
// surface that needs agents from every space the user can access.
// ---------------------------------------------------------------------------

export function useMeAgents() {
  return useQuery({
    queryKey: ['me', 'agents'],
    queryFn: () => meApi.agents(),
  });
}

/**
 * Look up an agent by id without knowing its space ahead of time. Backend
 * rule: single-entity reads live under `/spaces/{spaceId}/agents/{id}`,
 * so we resolve the owning space via the cached `/me/agents` list (which
 * returns each row with its `spaceId`), then call the space-scoped read.
 */
export function useMeAgent(agentId: string | undefined) {
  const { data: list } = useMeAgents();
  const cached = list?.items.find((a) => a.agentId === agentId);
  const spaceId = (cached as (Agent & { spaceId?: string }) | undefined)?.spaceId;
  return useQuery({
    queryKey: ['space', spaceId, 'agents', agentId],
    queryFn: () => spaceApi(spaceId!).agents.get(agentId!),
    enabled: !!agentId && !!spaceId,
  });
}

/**
 * Publish an agent without a SpaceProvider — caller passes the agent's
 * spaceId explicitly (typically read off `useMeAgent(agentId).data?.spaceId`).
 * Used by the operator-side Builder canvas/preview, which can publish into
 * any space the architect manages.
 */
export function usePublishAgentIn(spaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, description }: { agentId: string; description?: string }) => {
      if (!spaceId) throw new Error('spaceId required to publish agent');
      return spaceApi(spaceId).agents.publish(agentId, description);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['me', 'agents', variables.agentId] });
      if (spaceId) {
        queryClient.invalidateQueries({
          queryKey: ['space', spaceId, 'agents', variables.agentId, 'versions'],
        });
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Default exports — space-scoped is the right default for most callers.
// Operator/Builder-side files that need cross-space must call useMe* instead.
// ---------------------------------------------------------------------------

export const useAgents = useSpaceAgents;
export const useAgent = useSpaceAgent;
export const useCreateAgent = useCreateSpaceAgent;
export const useUpdateAgent = useUpdateSpaceAgent;
export const useDeleteAgent = useDeleteSpaceAgent;
export const usePublishAgent = usePublishSpaceAgent;
export const useAgentVersions = useSpaceAgentVersions;
export const useAgentVersion = useSpaceAgentVersion;
export const useDeleteAgentVersion = useDeleteSpaceAgentVersion;
export const useAgentAliases = useSpaceAgentAliases;
export const useCreateAgentAlias = useCreateSpaceAgentAlias;
export const useDeleteAgentAlias = useDeleteSpaceAgentAlias;
export const useInvokeAgent = useInvokeSpaceAgent;
export const useAgentsForSubAgentSelection = useSpaceAgentsForSubAgentSelection;
